import React, { useEffect, useState } from 'react';
import { authorizedPost, toggleLoading } from '../../store/actions';
import { Box, Button, CircularProgress, IconButton, MenuItem, Modal, Stack, Typography } from '@mui/material';
import { tableStyle } from '../../components';
import { DataGrid } from '@mui/x-data-grid';
import AvailabilityUpdation from '../../components/AvailabilityUpdation';
import {
    ACTIVATE_ROOMTYPE_PMS,
    ADD_ROOMTYPE_PMS,
    GET_AVAILABLE_PMS,
    LIST_ROOMTYPE_PMSS,
    UPDATE_ROOMTYPE_PMS,
} from '../../graphql';
import { Add, Edit } from '@mui/icons-material';
import Header from '../../components/Header';
import { CssTextField } from '../../components/TextField';
import { toast } from "react-toastify";

function RoomtypePms({ roomtypeId, dispatch, colors }) {
    const [pmsList, setPmsList] = useState([]);
    const [selectedPmsId, setSelectedPmsId] = useState("");
    const [pmsRoomtypeId, setPmsRoomtypeId] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [open, setOpen] = useState(false);
    // "add" | "edit"
    const [mode, setMode] = useState("add");

    // PMS dropdown options fetched from backend (get_available_pms)
    const [availablePmss, setAvailablePmss] = useState([]);
    const [pmsListLoading, setPmsListLoading] = useState(false);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [])

    const fetchData = async () => {
        dispatch(toggleLoading(true));
        let response = await authorizedPost(LIST_ROOMTYPE_PMSS, { roomtypeId: roomtypeId });
        if (response?.status && response?.data?.list_roomtype_pmss?.data) {
            setPmsList(response.data.list_roomtype_pmss.data);
        }
        dispatch(toggleLoading(false));
    }

    const fetchAvailablePmss = async () => {
        setPmsListLoading(true);
        const response = await authorizedPost(GET_AVAILABLE_PMS);
        if (response?.status && response?.data?.get_available_pms?.data) {
            setAvailablePmss(response.data.get_available_pms.data);
        }
        setPmsListLoading(false);
    }

    const handleOpenAddModal = () => {
        setMode("add");
        resetForm();
        setOpen(true);
        fetchAvailablePmss();
    }

    const handleOpenEditModal = (row) => {
        setMode("edit");
        // Pre-populate the form with existing values
        setSelectedPmsId(row?.pms_id ?? "");
        setPmsRoomtypeId(row?.pms_roomtype_id ?? "");
        setOpen(true);
        fetchAvailablePmss();
    }

    const resetForm = () => {
        setSelectedPmsId("");
        setPmsRoomtypeId("");
    }

    const handleClose = () => {
        setOpen(false);
        resetForm();
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedPmsId) {
            toast.error("Please select a PMS");
            return;
        }
        if (!pmsRoomtypeId?.trim()) {
            toast.error("PMS roomtype ID is required");
            return;
        }

        // Find the selected PMS's name from the available list
        const selectedPms = availablePmss.find((p) => String(p.id) === String(selectedPmsId));
        const pmsName = selectedPms?.name ?? "";

        const payload = {
            id: roomtypeId,
            pmsId: String(selectedPmsId),
            pmsName,
            pmsRoomtypeId: pmsRoomtypeId.trim(),
        };

        setIsLoading(true);
        const mutation = mode === "edit" ? UPDATE_ROOMTYPE_PMS : ADD_ROOMTYPE_PMS;
        const response = await authorizedPost(mutation, payload);
        setIsLoading(false);

        if (response?.status) {
            const responseKey = mode === "edit" ? "update_roomtype_pms" : "add_roomtype_pms";
            const fallback = mode === "edit" ? "PMS mapping updated" : "PMS mapped to roomtype";
            const message = response?.data?.[responseKey]?.message || fallback;
            toast.success(message);
            resetForm();
            setOpen(false);
            fetchData();
        }
        // Errors are toast'd globally by api.config.js
    }

    const handleToggleActivation = async (id) => {
        const response = await authorizedPost(ACTIVATE_ROOMTYPE_PMS, { id });
        if (response?.status) {
            const message = response?.data?.activate_roomtype_pms?.message || "PMS mapping updated";
            toast.success(message);
            fetchData();
        }
    }

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { sm: 400, xs: "90%" },
        bgcolor: colors.primary[400],
        outline: "none",
        boxShadow: 24,
        p: 2,
    };

    const columns = [
      {
        field: "pms_name",
        headerName: "PMS Name",
        cellClassName: "name-column--cell",
        width: 200,
        renderCell: ({ row: { pms_name } }) => pms_name || "-",
      },
      {
        field: "pms_roomtype_id",
        headerName: "PMS Roomtype ID",
        width: 220,
        renderCell: ({ row: { pms_roomtype_id } }) => pms_roomtype_id || "-",
      },
      {
        field: "is_active",
        headerName: "Availability",
        width: 130,
        renderCell: ({ row: { is_active, _id } }) => {
          return (
            <AvailabilityUpdation
              isActive={is_active}
              action={() => handleToggleActivation(_id)}
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <Button
            size="small"
            variant="contained"
            startIcon={<Edit fontSize="small" />}
            onClick={() => handleOpenEditModal(row)}
          >
            Update
          </Button>
        ),
      },
    ];

    // Hide "Add" button when a mapping already exists (one PMS per roomtype)
    const hasMapping = pmsList?.length > 0;

    return (
        <>
            <Box mb={15}>
                <Box
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"space-between"}
                    alignItems={"end"}
                    m="20px 0"
                >
                    <Typography variant='h4' fontWeight="bold">
                        PMS LIST
                    </Typography>
                    {!hasMapping && (
                        <Box>
                            <Button
                                sx={{
                                    backgroundColor: colors.blueAccent[700],
                                    color: colors.grey[100],
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    padding: { sm: "10px 20px", xs: "5px 10px" },
                                }}
                                onClick={handleOpenAddModal}
                            >
                                <Add sx={{ mr: "10px" }} />
                                Add new PMS
                            </Button>
                        </Box>
                    )}
                </Box>
                <Box
                    m="8px 0 0 0"
                    height="350px"
                    sx={tableStyle(colors)}
                >
                    <DataGrid
                        rows={pmsList}
                        columns={columns}
                        getRowId={(row) => row?._id}
                    />
                </Box>

                <Modal
                    open={open}
                    onClose={handleClose}
                >
                    <Box sx={modalStyle}>
                        <Header
                            title={mode === "edit" ? "EDIT PMS" : "ADD NEW PMS"}
                            titleVariant="h4"
                        />
                        <form onSubmit={handleSubmit}>
                            <Stack spacing={2}>
                                <CssTextField
                                    fullWidth
                                    required
                                    select
                                    size="small"
                                    label="PMS"
                                    value={selectedPmsId ?? ""}
                                    onChange={(e) => setSelectedPmsId(e.target.value)}
                                    disabled={pmsListLoading}
                                >
                                    {pmsListLoading ? (
                                        <MenuItem value="" disabled>Loading...</MenuItem>
                                    ) : availablePmss?.length ? (
                                        availablePmss.map((pms) => (
                                            <MenuItem value={pms.id} key={pms.id}>
                                                {pms.name}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem value="" disabled>No PMSs available</MenuItem>
                                    )}
                                </CssTextField>
                                <CssTextField
                                    fullWidth
                                    required
                                    size="small"
                                    type="text"
                                    label="PMS Roomtype ID"
                                    placeholder="PMS Roomtype ID"
                                    value={pmsRoomtypeId ?? ""}
                                    onChange={(e) => setPmsRoomtypeId(e.target.value?.replace(" ", ""))}
                                />
                                <Button
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    color="secondary"
                                    variant="contained"
                                    sx={{ fontSize: "15px" }}
                                    disabled={isLoading}
                                >
                                    {isLoading ? <CircularProgress color="inherit" size={24} /> : <b>{mode === "edit" ? "Update" : "Submit"}</b>}
                                </Button>
                            </Stack>
                        </form>
                    </Box>
                </Modal>
            </Box>
        </>
    )
}

export default RoomtypePms;
